import React, { Suspense, useEffect } from "react";
import { Outlet, useLocation, useNavigationType } from "react-router-dom";
import { HeadProvider } from "../contexts/HeadProvider";
import { LanguageProvider } from "../contexts/LanguageContext";
import { HubSpotIdentifyFromUrl } from "./analytics/HubSpotIdentifyFromUrl";
import { TagManagerPageTracker } from "./analytics/TagManagerPageTracker";
import { GlobalStructuredData } from "./GlobalStructuredData";
import { PageLoader } from "./ui/PageLoader";

/**
 * Wie lange auf ein Ankerziel gewartet und die Position nachgefuehrt wird.
 *
 * Deckt zwei Faelle ab, die beide beim Klick auftreten und beim Reload nicht:
 *
 * 1. Die Zielseite kommt aus einem lazy-Chunk hinter `Suspense` — im Moment der
 *    Navigation rendert noch der `PageLoader`, `getElementById` findet nichts.
 * 2. Die Seite waechst nach dem Scrollen weiter, weil noch nicht alle Sektionen
 *    ausgemessen sind. Ein einmaliger Sprung landet dann zu hoch.
 *
 * 3 Sekunden sind grosszuegig fuer einen lokalen Chunk und kurz genug, dass ein
 * ins Leere zeigender Anker nicht dauerhaft nachgefuehrt wird.
 */
const ANKER_WARTEZEIT_MS = 3000;

/**
 * ⚠️ `instant` — weder `smooth` noch `auto`.
 *
 * Am 11.8.2026 im Browser gemessen, auf `/angebote#einstiegspfad` mit einer
 * Distanz von 4312px:
 *
 * | `behavior`  | Ergebnis (`window.scrollY`)      |
 * | ----------- | -------------------------------- |
 * | `"smooth"`  | **0** — nichts passiert          |
 * | `"auto"`    | **1408** — bleibt auf halbem Weg |
 * | `"instant"` | 4312 — am Ziel                   |
 *
 * Ursache ist der berechnete `scroll-behavior: smooth` auf `html` (Tailwind-
 * Preflight) zusammen mit `overflow-x: clip` auf `html`/`body`
 * (`styles/index.css`): Chrome bricht den sanften Scroll des Wurzel-Elements
 * ab, waehrend ein sofortiger durchgeht.
 *
 * `auto` genuegt NICHT — das war der erste Anlauf: «auto» bedeutet «nimm das
 * CSS», und das CSS sagt hier eben `smooth`. Der Sprung blieb dann bei 1408 von
 * 4312 stehen, auf `/angebote` sichtbar als Landung bei «Product-Market Test»
 * statt bei der Workshop-Leiter.
 *
 * Nur `instant` ueberstimmt das CSS. Wer hier auf `smooth` oder `auto`
 * zurueckstellt, weil es weicher aussieht, bricht den Sprung wieder — und merkt
 * es an keinem Test, weil jeder Reload und jeder `page.goto()`-Lauf gruen
 * bleibt und jsdom kein Layout hat.
 *
 * (`FaqSection` benutzt weiterhin `smooth` und funktioniert dort: kurze
 * Distanzen innerhalb einer bereits sichtbaren Sektion.)
 *
 * Der Preis ist ein harter Sprung statt einer Animation. Das ist die
 * Gegenleistung dafuer, dass der Sprung ueberhaupt ankommt.
 */
const SCROLL_VERHALTEN: ScrollBehavior = "instant";

/**
 * Setzt die Scrollposition bei jedem Routenwechsel.
 *
 * Ohne Hash: an den Seitenanfang. Mit Hash: auf das Ankerziel — **das war bis
 * zum 11.8.2026 nicht der Fall.** Der Effekt unterdrueckte bei einem Hash nur
 * das Zurueckspringen nach oben und tat sonst nichts. Ergebnis: Ein Klick auf
 * `/angebote#einstiegspfad` wechselte die Route, ohne zu scrollen — der
 * Besucher landete oben. Erst ein Reload sprang richtig, weil dann der BROWSER
 * scrollt und nicht der Router.
 *
 * Genau das macht die Klasse dieses Fehlers aus: Beim Neuladen funktioniert
 * alles, jeder Direktaufruf und jeder Test mit `page.goto()` ist gruen. Kaputt
 * ist nur der Weg, den ein echter Besucher nimmt — der Klick.
 */
/**
 * Scrollposition je History-Eintrag, in `sessionStorage`.
 *
 * **Warum nicht eine Map im Modul.** Erster Anlauf war genau das. Im Browser
 * gemessen (9.9.2026): Der Zurueck-Knopf loest hier eine ECHTE
 * Dokument-Navigation aus — die Navigation Timing API meldet `back_forward`
 * mit `startTime: 0`, also einen frischen Ladevorgang. Damit ist jede
 * Modulvariable weg, bevor sie gelesen werden koennte, und React Router sieht
 * gar kein `POP`: Die Anwendung startet neu.
 *
 * `sessionStorage` ueberlebt das, gilt pro Tab und wird beim Schliessen
 * verworfen — genau die Lebensdauer, die eine Scrollposition braucht.
 *
 * **Der Schluessel ist der Pfad**, nicht `Location.key`: Nach einem
 * Dokumentwechsel sind die `key`s neu vergeben, ein gemerkter waere nach dem
 * Zurueck nicht wiederzufinden. Der Preis ist bekannt und klein — zwei Besuche
 * derselben Seite im Verlauf teilen sich einen Eintrag.
 */
const SPEICHER = "quatico:scrollpositionen";

const positionenLesen = (): Record<string, number> => {
    try {
        return JSON.parse(sessionStorage.getItem(SPEICHER) ?? "{}") as Record<string, number>;
    } catch {
        // Privates Fenster, gesperrter Speicher: ohne Wiederherstellung
        // weiterlaufen ist besser als eine Seite, die nicht rendert.
        return {};
    }
};

const positionMerken = (pfad: string, y: number): void => {
    try {
        sessionStorage.setItem(SPEICHER, JSON.stringify({ ...positionenLesen(), [pfad]: y }));
    } catch {
        /* siehe oben */
    }
};

export const ScrollToTop = () => {
    const { pathname, hash } = useLocation();
    const navType = useNavigationType();

    useEffect(() => {
        /*
         * Eigene Buchfuehrung statt der nativen Restoration (9.9.2026).
         *
         * Erster Anlauf war `scrollRestoration = "auto"` — die Flagge stand
         * richtig, und die Position kam trotzdem nicht zurueck. Im Browser
         * gemessen: `history.scrollRestoration === "auto"`, Navigationstyp
         * `back_forward`, NULL `scrollTo`-Aufrufe von uns, Seitenhoehe 20'821 px
         * konstant — und `scrollY === 0`. Die native Restoration ist fuer
         * Dokument-Navigationen gebaut; bei einer clientseitigen Navigation, wo
         * React Router `pushState` selbst ruft, greift sie nicht.
         *
         * `manual` ist deshalb richtig: Wir stellen selbst wieder her. Ohne das
         * Flag wuerde der Browser zusaetzlich hineinregieren.
         */
        window.history.scrollRestoration = "manual";
    }, []);

    // Derselbe Anker ein zweites Mal.
    //
    // Klickt jemand `#kontakt` im Header, waehrend er schon auf der Seite ist
    // und weitergescrollt hat, aendern sich weder `pathname` noch `hash` — der
    // Effekt unten laeuft nicht, und nichts passiert. Der Browser loest dafuer
    // `hashchange` aus. Bestandsverhalten, nicht neu mit dem Anker-Fix, aber es
    // betrifft einen Link auf JEDER Seite.
    useEffect(() => {
        const beiHashWechsel = () => {
            const id = decodeURIComponent(window.location.hash.slice(1));
            document.getElementById(id)?.scrollIntoView({ behavior: SCROLL_VERHALTEN, block: "start" });
        };

        window.addEventListener("hashchange", beiHashWechsel);
        return () => window.removeEventListener("hashchange", beiHashWechsel);
    }, []);

    /*
     * Die Position LAUFEND festhalten, nicht erst beim Verlassen.
     *
     * Erster Anlauf merkte sie in der Aufraeumfunktion des Effekts. Das traegt
     * nicht: Der Zurueck-Knopf loest hier eine echte Dokument-Navigation aus
     * (gemessen 9.9.2026), und beim Wegnavigieren laeuft keine Aufraeumfunktion
     * mehr, deren Ergebnis den Ladevorgang ueberlebt.
     *
     * Also bei jedem Scrollen schreiben, gedrosselt ueber
     * `requestAnimationFrame` — ein `sessionStorage.setItem` je Scroll-Event
     * waere Dutzende Schreibvorgaenge pro Sekunde.
     */
    useEffect(() => {
        let angefordert = false;
        let entfernt = false;

        const beiScroll = () => {
            if (angefordert || entfernt) {
                return;
            }
            angefordert = true;
            requestAnimationFrame(() => {
                angefordert = false;
                /*
                 * ⚠️ NUR eine Position ungleich null merken.
                 *
                 * Gemessen am 9.9.2026: Beim Klick auf einen Link scrollt der
                 * Effekt unten auf 0, das loest ein `scroll`-Ereignis aus, und
                 * dieser Zuhoerer — noch mit dem ALTEN `pathname` registriert —
                 * schrieb die 0 ueber die gute Position. Der Speicher ging von
                 * `{"/agentic-development/": 4000}` auf `{"…": 0}`, und die
                 * Wiederherstellung fand nichts mehr zum Wiederherstellen.
                 *
                 * Eine 0 ist ohnehin nichts, was wiederhergestellt werden
                 * muesste: Der Seitenanfang ist der Ausgangszustand.
                 */
                if (entfernt || window.scrollY === 0) {
                    return;
                }
                positionMerken(pathname, window.scrollY);
            });
        };

        window.addEventListener("scroll", beiScroll, { passive: true });
        return () => {
            entfernt = true;
            window.removeEventListener("scroll", beiScroll);
        };
    }, [pathname]);

    useEffect(() => {
        /*
         * Zurueck/Vorwaerts erkennen — auf ZWEI Wegen.
         *
         * `navType === "POP"` deckt die clientseitige Navigation ab. Kommt der
         * Besucher ueber eine echte Dokument-Navigation zurueck (was der
         * Zurueck-Knopf hier ausloest), startet die Anwendung neu und React
         * Router meldet kein POP — dann sagt es die Navigation Timing API.
         */
        /*
         * `getEntriesByType` ist nicht ueberall da — jsdom kennt es nicht, und
         * aeltere Browser liefern die Navigation Timing API in einer anderen
         * Form. Ein ungeprueftes Aufrufen warf hier in ALLEN zehn Jest-Tests
         * (gemessen 9.9.2026), und ein Fehler in diesem Effekt nimmt die ganze
         * Seite mit. Fehlt die API, entscheidet allein `navType`.
         */
        const eintrag =
            typeof performance?.getEntriesByType === "function"
                ? (performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined)
                : undefined;
        const zurueckGeladen = eintrag?.type === "back_forward";

        if (navType === "POP" || zurueckGeladen) {
            /*
             * Zurueck oder Vorwaerts: die gemerkte Position wiederherstellen.
             *
             * Nachgefuehrt statt einmal gesprungen, aus demselben Grund wie bei
             * der Anker-Navigation unten: Beim Wiederherstellen sind noch nicht
             * alle Sektionen ausgemessen, die Seite waechst danach weiter, und
             * ein einzelnes `scrollTo` landete zu kurz. Abbruch, sobald die
             * Position zwei Frames haelt — oder nach 60 Frames (~1 s), damit
             * eine Seite, die dauernd waechst, nicht ewig nachzieht.
             */
            const ziel = positionenLesen()[pathname];
            if (ziel === undefined || ziel === 0) {
                return;
            }

            let ruhig = 0;
            let versuche = 0;
            let frame = 0;

            const nachziehen = () => {
                if (Math.abs(window.scrollY - ziel) < 2) {
                    ruhig += 1;
                    if (ruhig >= 2) {
                        return;
                    }
                } else {
                    ruhig = 0;
                    window.scrollTo({ top: ziel, left: 0, behavior: "instant" });
                }

                versuche += 1;
                if (versuche < 60) {
                    frame = requestAnimationFrame(nachziehen);
                }
            };

            frame = requestAnimationFrame(nachziehen);
            return () => cancelAnimationFrame(frame);
        }

        if (!hash) {
            window.scrollTo({ top: 0, left: 0, behavior: "instant" });
            return;
        }

        const ziel = () => document.getElementById(decodeURIComponent(hash.slice(1)));

        /*
         * Nachfuehren, nicht einmal springen.
         *
         * ⚠️ Ein einzelner `scrollIntoView` landet ZU FRUEH — im Browser am
         * 11.8.2026 gemessen: Der Klick auf die vierte Wegweiser-Karte scrollte
         * auf `/angebote`, hielt aber bei «Product-Market Test» statt bei der
         * Workshop-Leiter. Beim Scrollen sind noch nicht alle Sektionen
         * ausgemessen; die Seite waechst danach unter der Scrollposition
         * weiter, und das Ziel rutscht nach unten weg.
         *
         * Also so lange nachziehen, bis das Ziel zwei Frames in Folge dort
         * bleibt, wo es hingehoert. `requestAnimationFrame` statt Intervall:
         * Es laeuft im Takt des Layouts, nicht daneben.
         */
        let ruhigeFrames = 0;
        let rafId = 0;
        const start = performance.now();

        const nachfuehren = () => {
            const element = ziel();

            if (!element) {
                // Zielseite noch im lazy-Chunk — weiter warten, bis die Frist um ist.
                if (performance.now() - start < ANKER_WARTEZEIT_MS) {
                    rafId = requestAnimationFrame(nachfuehren);
                }
                return;
            }

            const abstand = element.getBoundingClientRect().top;

            // Ein Pixel Toleranz: subpixelgenaue Layouts erreichen nie exakt 0.
            if (Math.abs(abstand) <= 1) {
                ruhigeFrames += 1;
                if (ruhigeFrames >= 2) {
                    return;
                }
            } else {
                ruhigeFrames = 0;
                element.scrollIntoView({ behavior: SCROLL_VERHALTEN, block: "start" });
            }

            if (performance.now() - start < ANKER_WARTEZEIT_MS) {
                rafId = requestAnimationFrame(nachfuehren);
            }
        };

        nachfuehren();

        return () => cancelAnimationFrame(rafId);
        // `navType` gehoert dazu: Wechselt der Navigationstyp bei gleichem
        // Pfad, entscheidet er ueber Wiederherstellen statt Hochscrollen.
    }, [pathname, hash, navType]);

    return null;
};

export const AppLayout = () => (
    <HeadProvider>
        <LanguageProvider>
            <GlobalStructuredData />
            <HubSpotIdentifyFromUrl />
            <TagManagerPageTracker />
            <ScrollToTop />
            <Suspense fallback={<PageLoader />}>
                <Outlet />
            </Suspense>
        </LanguageProvider>
    </HeadProvider>
);
